import AOS from 'aos';

import debounce from '../util/debounce';
import {setVars} from '../globals/height';

import RangeSlider from './range-slider';
import {makeSwatches} from './swatch';
import {ProductGrid} from './product-grid';
import {Collapsible} from './collapsible';
import {Ajaxify} from './ajaxinate';

const settings = {
  loadingTimeout: 300,
};

const selectors = {
  buttons: 'button',
  buttonFiltersToggle: '[data-toggle-filters]',
  buttonFiltersClose: '[data-close-filters]',
  buttonFiltersOpen: '[data-open-filters]',
  collectionWrapper: '[data-collection-wrapper]',
  inputs: 'input, select, label, textarea',
  inputSort: '[data-input-sort]',
  filters: '[data-collection-filters]',
  filtersWrapper: '[data-collection-filters-wrapper]',
  filtersList: '[data-collection-filters-list]',
  filtersStickyBar: '[data-collection-sticky-bar]',
  filter: '[data-collection-filter]',
  filterTag: '[data-collection-filter-tag]',
  filterTagButton: '[data-collection-filter-tag-button]',
  filtersForm: '[data-collection-filters-form]',
  filterResetButton: '[data-filter-reset-button]',
  filterTagClearButton: '[data-filter-tag-reset-button]',
  popupsSection: '[data-section-type="popups"]',
  productGrid: '[data-collection-products]',
  productMediaSlideshow: '[data-product-media-slideshow]',
  productMediaSlide: '[data-product-media-slideshow-slide]',
  priceMin: '[data-field-price-min]',
  priceMax: '[data-field-price-max]',
  rangeMin: '[data-se-min-value]',
  rangeMax: '[data-se-max-value]',
  rangeMinValue: 'data-se-min-value',
  rangeMaxValue: 'data-se-max-value',
  rangeMinDefault: 'data-se-min',
  rangeMaxDefault: 'data-se-max',
  searchForm: '[data-search-form]',
  swatchesWrapper: '[data-grid-swatches]',
  swatch: '[data-swatch]',
};

const classes = {
  filtersVisible: 'collection__filters--visible',
  isActive: 'is-active',
  isExpanded: 'is-expanded',
  isVisible: 'is-visible',
  isLoading: 'is-loading',
  popupIsVisible: 'popup--visible',
};

const attributes = {
  preventScrollLock: 'data-prevent-scroll-lock',
};

const sections = {};

class Filters {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.enableFilters = container.dataset.enableFilters === 'true';
    this.filterMode = container.dataset.filterMode;
    this.collectionHandle = this.container.dataset.collection;
    this.productGrid = this.container.querySelector(selectors.productGrid);
    this.filters = this.container.querySelector(selectors.filters);
    this.filtersStickyBar = this.container.querySelector(selectors.filtersStickyBar);
    this.filtersForm = this.container.querySelector(selectors.filtersForm);
    this.searchForm = this.container.querySelector(selectors.searchForm);
    this.inputSort = this.container.querySelectorAll(selectors.inputSort);
    this.filterData = [];
    this.rangeSlider = null;
    this.hideFilters = this.hideFilters.bind(this);
    this.showFilters = this.showFilters.bind(this);
    this.onFilterResetClick = this.onFilterResetClick.bind(this);
    this.onFilterTagResetClick = this.onFilterTagResetClick.bind(this);
    this.onFilterTagClearClick = this.onFilterTagClearClick.bind(this);
    this.onFilterToggleClick = this.onFilterToggleClick.bind(this);
    this.onKeyUpHandler = this.onKeyUpHandler.bind(this);
    this.updateRangeEvent = this.updateRange.bind(this);
    this.debouncedSubmitEvent = debounce(function (event) {
      this.onSubmitHandler(event);
    }, 500);
    this.debouncedSortEvent = debounce(function (event) {
      this.onSortChange(event);
    }, 500);
    this.productGridEvents = {};

    this.initTagFilters();
    this.initFacetedFilters();
    this.bindToggleButtonsEvents();
    this.bindFilterButtonsEvents();
    this.initProductGridEvents();

    makeSwatches(this.container);
    this.collapsible = new Collapsible(this.container);

    // Update css variable for collection sticky bar height
    setVars();

    window.addEventListener('popstate', this.onHistoryChange.bind(this));
  }

  /*
   * Init faceted filters
   */
  initFacetedFilters() {
    if (this.filterMode == 'tag' || this.filterMode == 'group' || !this.enableFilters) {
      return;
    }

    this.rangeSlider = new RangeSlider(this.container);
  }

  /*
   * Price range slider update
   */
  updateRange() {
    const rangeMin = this.filtersForm.querySelector(selectors.rangeMin);
    const rangeMax = this.filtersForm.querySelector(selectors.rangeMax);
    const priceMin = this.filtersForm.querySelector(selectors.priceMin);
    const priceMax = this.filtersForm.querySelector(selectors.priceMax);

    if (rangeMin.hasAttribute(selectors.rangeMinValue) && rangeMax.hasAttribute(selectors.rangeMaxValue)) {
      const priceMinValue = parseFloat(priceMin.placeholder, 10);
      const priceMaxValue = parseFloat(priceMax.placeholder, 10);
      const rangeMinValue = parseFloat(rangeMin.getAttribute(selectors.rangeMinValue), 10);
      const rangeMaxValue = parseFloat(rangeMax.getAttribute(selectors.rangeMaxValue), 10);

      if (priceMinValue !== rangeMinValue || priceMaxValue !== rangeMaxValue) {
        priceMin.value = parseInt(rangeMinValue);
        priceMax.value = parseInt(rangeMaxValue);

        this.filtersForm.dispatchEvent(new Event('input', {bubbles: true}));
      }
    }
  }

  /*
   * Render product grid and filters on form submission
   */
  onSubmitHandler(e) {
    e.preventDefault();
    const formData = new FormData(e.target.closest('form'));
    const searchParams = new URLSearchParams(formData);

    // if submitted price equal to price range min and max remove price parameters
    const rangeMin = this.filtersForm.querySelector(selectors.rangeMin);
    const rangeMax = this.filtersForm.querySelector(selectors.rangeMax);
    const priceMin = this.filtersForm.querySelector(selectors.priceMin);
    const priceMax = this.filtersForm.querySelector(selectors.priceMax);
    const checkElements = rangeMin && rangeMax && priceMin && priceMax;

    if (checkElements && rangeMin.hasAttribute(selectors.rangeMinDefault) && rangeMax.hasAttribute(selectors.rangeMaxDefault)) {
      const rangeMinDefault = parseFloat(rangeMin.getAttribute(selectors.rangeMinDefault), 10);
      const rangeMaxDefault = parseFloat(rangeMax.getAttribute(selectors.rangeMaxDefault), 10);
      const priceMinValue = !priceMin.value ? rangeMinDefault : parseFloat(priceMin.value, 10);
      const priceMaxValue = !priceMax.value ? rangeMaxDefault : parseFloat(priceMax.value, 10);

      if (priceMinValue <= rangeMinDefault && priceMaxValue >= rangeMaxDefault) {
        searchParams.delete('filter.v.price.gte');
        searchParams.delete('filter.v.price.lte');
      }
    }

    this.renderSection(searchParams.toString(), e);
  }

  /*
   * Call renderSection on history change
   */
  onHistoryChange(e) {
    if (!this.filters) {
      return;
    }

    const searchParams = e.state?.searchParams || '';
    this.renderSection(searchParams, null, false);
  }

  /*
   * Render section on history change or filter/sort change event
   */
  renderSection(searchParams, event, updateURLHash = true) {
    this.startLoading();
    const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`;
    const filterDataUrl = (element) => element.url === url;
    this.filterData.some(filterDataUrl) ? this.renderSectionFromCache(filterDataUrl, event) : this.renderSectionFromFetch(url, event);

    if (updateURLHash) {
      this.updateURLHash(searchParams);
    }
  }

  /*
   * Render section from fetch call
   */
  renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        this.filterData = [...this.filterData, {html, url}];
        this.renderFilters(html);
        this.renderProductGrid(html);
        this.finishLoading();
      });
  }

  /*
   * Render section from Cache
   */
  renderSectionFromCache(filterDataUrl, event) {
    const html = this.filterData.find(filterDataUrl).html;
    this.renderFilters(html, event);
    this.renderProductGrid(html);
    this.finishLoading();
  }

  /*
   * Render product grid items on fetch call
   */
  renderProductGrid(html) {
    const newProductGrid = new DOMParser().parseFromString(html, 'text/html').querySelector(selectors.productGrid);

    if (!newProductGrid) {
      return;
    }

    this.productGrid.innerHTML = newProductGrid.innerHTML;

    this.initProductGridEvents();
  }

  /*
   * Render filters on fetch call
   */
  renderFilters(html) {
    const newFilters = new DOMParser().parseFromString(html, 'text/html').querySelector(selectors.filters);

    if (!newFilters) {
      return;
    }

    this.filters.innerHTML = newFilters.innerHTML;
    this.filtersForm = document.querySelector(selectors.filtersForm);
    this.bindFilterButtonsEvents();
    this.bindToggleButtonsEvents();
    makeSwatches(this.container);
    this.collapsible = new Collapsible(this.container);

    // Init price range slider
    document.dispatchEvent(new CustomEvent('theme:filters:init', {bubbles: true}));
  }

  /*
   * Update URL when filter/sort is changed
   */
  updateURLHash(searchParams) {
    history.pushState({searchParams}, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  /*
   * Bind filter buttons events
   */
  bindFilterButtonsEvents() {
    this.container.querySelectorAll(selectors.filterResetButton).forEach((button) => {
      button.addEventListener('click', this.onFilterResetClick, {once: true});
    });

    if (this.filtersForm) {
      this.filtersForm.addEventListener('input', this.debouncedSubmitEvent.bind(this));
      this.filtersForm.addEventListener('theme:filter:range-update', this.updateRangeEvent);
    }
  }

  /*
   * Render products on specific filter click event
   */
  onFilterResetClick(e) {
    e.preventDefault();
    this.renderSection(new URL(e.currentTarget.href).searchParams.toString());
  }

  /*
   * Bind filter title click events to toggle options visibility
   */
  bindToggleButtonsEvents() {
    this.container.querySelectorAll(selectors.buttonFiltersToggle).forEach((button) => {
      button.addEventListener('click', this.onFilterToggleClick);
    });
    this.container.querySelectorAll(selectors.buttonFiltersClose).forEach((button) => {
      button.addEventListener('click', this.hideFilters);
    });
    this.container.querySelectorAll(selectors.buttonFiltersOpen).forEach((button) => {
      button.addEventListener('click', this.showFilters);
    });

    this.container.querySelector(selectors.collectionWrapper).addEventListener('keyup', this.onKeyUpHandler);
  }

  /*
   * Event handler on user ESC key press
   */
  onKeyUpHandler(e) {
    if (e.keyCode === theme.keyboardKeys.ESCAPE) {
      this.hideFilters();
    }
  }

  /*
   * Toggle filter options on title click
   */
  onFilterToggleClick(e) {
    e.preventDefault();

    const filtersVisible = this.filters.classList.contains(classes.filtersVisible);

    filtersVisible ? this.hideFilters() : this.showFilters();
  }

  /*
   * Scroll down and open collection filters if they are hidden
   */
  showFilters() {
    const instance = this;
    const scrollableElement = document.querySelector(selectors.filtersList);

    slate.a11y.state.trigger = document.querySelector(selectors.buttonFiltersToggle);

    // Trap focus
    slate.a11y.trapFocus({
      container: instance.filters,
    });

    // Open filters and scroll lock if only they are hidden on lower sized screens
    if (window.innerWidth < theme.sizes.widescreen) {
      this.filters.classList.add(classes.filtersVisible);

      document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: scrollableElement}));
    }

    // Scroll filters into view
    if (this.filtersStickyBar) {
      let stickyBarOffsetTop = this.getStickyBarOffsetTop();

      window.scrollTo({
        top: stickyBarOffsetTop,
        left: 0,
        behavior: 'smooth',
      });
    }
  }

  /*
   * Hide filters dropdown
   */
  hideFilters() {
    let filtersVisible = this.filters.classList.contains(classes.filtersVisible);
    let loading = this.container.classList.contains(classes.isLoading);

    if (filtersVisible) {
      this.filters.classList.remove(classes.filtersVisible);
      slate.a11y.removeTrapFocus();
    }

    // Enable page scroll if no loading state
    if (!loading) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true, detail: settings.loadingTimeout}));
    }
  }

  /*
   * Init functions required when "Filter by tag/group" is selected from Collection page -> Collection pages -> Filter mode
   */
  initTagFilters() {
    if ((this.filterMode != 'tag' && this.filterMode != 'group') || !this.enableFilters) {
      return;
    }

    this.tags = this.container.dataset.tags.split('+').filter((item) => item);
    this.bindFilterTagButtonsEvents();
    this.bindSortChangeEvent();
    this.resizeEvent = () => this.hideFilters();

    document.addEventListener('theme:resize:width', this.resizeEvent);
  }

  /*
   * Render products when tag filter is selected
   */
  renderTagFiltersProducts(url) {
    this.startLoading();

    if (typeof this.endlessCollection === 'object') {
      this.endlessCollection.unload();
    }

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        const parsedData = new DOMParser().parseFromString(html, 'text/html');
        const productsHTML = parsedData.querySelector(selectors.productGrid).innerHTML;
        const filtersHTML = parsedData.querySelector(selectors.filters).innerHTML;

        this.productGrid.innerHTML = productsHTML;
        this.filters.innerHTML = filtersHTML;
        this.inputSort = this.container.querySelectorAll(selectors.inputSort);
        this.filtersForm = document.querySelector(selectors.filtersForm);
        this.filterData = [...this.filterData, {html, url}];

        this.bindFilterTagButtonsEvents();
        this.bindSortChangeEvent();
        this.bindToggleButtonsEvents();
        this.initProductGridEvents();
        makeSwatches(this.container);
        this.collapsible = new Collapsible(this.container);
        AOS.refreshHard();

        // Update page URL if supported by the browser
        if (history.replaceState) {
          window.history.pushState({path: url}, '', url);
        }
      })
      .catch((error) => {
        this.finishLoading();
        console.log(`Error: ${error}`);
      });
  }

  /*
   * Bind Filter by tag buttons
   */
  bindFilterTagButtonsEvents() {
    this.container.querySelectorAll(selectors.filterTagButton).forEach((button) => {
      button.addEventListener('click', this.onFilterTagButtonClick.bind(this), {once: true});
    });

    this.container.querySelectorAll(selectors.filterTagClearButton).forEach((button) => {
      button.addEventListener('click', this.onFilterTagClearClick, {once: true});
    });

    this.container.querySelectorAll(selectors.filterResetButton).forEach((button) => {
      button.addEventListener('click', this.onFilterTagResetClick, {once: true});
    });
  }

  /*
   * Bind input Sort by change event for "filters by tag/group" only
   */
  bindSortChangeEvent() {
    this.container.querySelectorAll(selectors.inputSort).forEach((input) => {
      input.addEventListener('input', this.debouncedSortEvent.bind(this));
    });
  }

  /*
   * Filter by tag buttons click event
   */
  onFilterTagButtonClick(e) {
    e.preventDefault();
    const button = e.currentTarget;
    const selectedTag = button.dataset.tag;
    let isTagSelected = button.parentNode.classList.contains(classes.isActive);

    if (isTagSelected) {
      let tagIndex = this.tags.indexOf(selectedTag);

      button.parentNode.classList.remove(classes.isActive);

      if (tagIndex > -1) {
        this.tags.splice(tagIndex, 1);
      }
    } else {
      button.parentNode.classList.add(classes.isActive);

      this.tags.push(selectedTag);
    }

    let url = this.collectionHandle + '/' + this.tags.join('+') + '?sort_by=' + this.getSortValue();

    // Close filters dropdown on tag select
    this.container.querySelector(selectors.filter).classList.remove(classes.isExpanded);
    this.container.querySelector(selectors.filter).setAttribute('aria-expanded', false);
    this.container.setAttribute('data-tags', '[' + this.tags + ']');
    this.renderTagFiltersProducts(url);
  }

  /*
   * Remove a specific tag filter
   */
  onFilterTagClearClick(e) {
    e.preventDefault();
    const button = e.currentTarget;
    const selectedTag = button.dataset.tag;
    const tagIndex = this.tags.indexOf(selectedTag);

    if (tagIndex > -1) {
      this.tags.splice(tagIndex, 1);
    }
    const url = this.collectionHandle + '/' + this.tags.join('+') + '?sort_by=' + this.getSortValue();

    this.container.setAttribute('data-tags', '[' + this.tags + ']');
    this.renderTagFiltersProducts(url);
  }

  /*
   * Re-render products with the new sort option selected
   */
  onSortChange() {
    let url = this.collectionHandle + '/' + this.tags.join('+') + '?sort_by=' + this.getSortValue();

    this.renderTagFiltersProducts(url);
  }

  /*
   * Get the selected sort option value
   */
  getSortValue() {
    let sortValue = '';
    this.inputSort.forEach((input) => {
      if (input.checked) {
        sortValue = input.value;
      }
    });

    return sortValue;
  }

  /*
   * Filter by tag reset button click event
   */
  onFilterTagResetClick(e) {
    e.preventDefault();

    this.container.querySelectorAll(selectors.filterTag).forEach((element) => {
      element.classList.remove(classes.isActive);
    });

    this.container.querySelectorAll(selectors.filter).forEach((element) => {
      element.classList.remove(classes.isExpanded);
      element.setAttribute('aria-expanded', false);
    });

    // Reset saved tags
    this.tags = [];
    this.container.setAttribute('data-tags', '');

    let url = this.collectionHandle + '/?sort_by=' + this.getSortValue();

    this.renderTagFiltersProducts(url);
  }

  /*
   * Get products container top position
   */
  getProductsOffsetTop() {
    return this.productGrid.getBoundingClientRect().top - document.body.getBoundingClientRect().top - this.filtersStickyBar.offsetHeight;
  }

  /*
   * Get collection page sticky bar top position
   */
  getStickyBarOffsetTop() {
    return this.filtersStickyBar.getBoundingClientRect().top - document.body.getBoundingClientRect().top;
  }

  /*
   * Init all the events required on product grid items
   */
  initProductGridEvents() {
    this.productGridEvents = new ProductGrid(this.container);

    if (theme.settings.enableInfinityScroll) {
      this.initInfinityScroll();
    }

    // Stop loading animation
    setTimeout(() => {
      this.finishLoading();
    }, settings.loadingTimeout * 1.5);
  }

  /*
   * Init Infinity scroll functionality
   */
  initInfinityScroll() {
    this.endlessCollection = new Ajaxify(this.container);
    this.endlessCollection.endlessScroll.settings.callback = () => {
      this.initProductGridEvents();
    };
  }

  /*
   * Show loading animation and lock body scroll
   */
  startLoading() {
    this.container.classList.add(classes.isLoading);
    this.hideFilters();

    document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));

    let productsTop = this.getProductsOffsetTop();

    window.scrollTo({
      top: productsTop,
      left: 0,
      behavior: 'smooth',
    });
  }

  /*
   * Hide loading animation and unlock body scroll
   */
  finishLoading() {
    const popups = document.querySelectorAll(`${selectors.popupsSection} .${classes.popupIsVisible}`);
    const isPopupActive = popups.length > 0;

    this.container.classList.remove(classes.isLoading);

    // Unlock the scroll unless there is a visible popup or there are only popups of types 'bar' and 'cookie'
    if (isPopupActive) {
      let preventScrollPopupsCount = 0;
      [...popups].forEach((popup) => {
        if (popup.hasAttribute(attributes.preventScrollLock)) {
          preventScrollPopupsCount += 1;
        }
      });

      if (preventScrollPopupsCount === popups.length) {
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true, detail: settings.loadingTimeout}));
      }
    } else {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true, detail: settings.loadingTimeout}));
    }

    AOS.refreshHard();
  }

  /*
   * On block:deselect event
   */
  onDeselect() {
    if (this.productGridEvents) {
      this.productGridEvents.onDeselect();
    }
  }

  /*
   * On section:unload event
   */
  onUnload() {
    if (typeof this.endlessCollection === 'object') {
      this.endlessCollection.unload();
    }

    if (this.productGridEvents) {
      this.productGridEvents.onUnload();
    }

    this.collapsible.onUnload();

    if (this.rangeSlider) {
      this.rangeSlider.unload();
    }

    document.removeEventListener('theme:resize:width', this.resizeEvent);
  }
}

const filters = {
  onLoad() {
    sections[this.id] = new Filters(this.container);
  },
  onDeselect() {
    sections[this.id].onDeselect();
  },
  onUnload() {
    sections[this.id].onUnload();
  },
};

export default filters;
